import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useLang } from '../../contexts/LangContext';
import { QUESTIONARIOS, totalItens } from '../../lib/questionarios';

// Respostas vazias no formato de cada modo
function respostasVazias(q) {
  if (q.secoes) return q.secoes.reduce((acc, s) => ({ ...acc, [s.id]: s.itens.map(() => null) }), {});
  return q.perguntas.map(() => null);
}

function contarRespondidas(q, respostas) {
  if (q.secoes) return Object.values(respostas).flat().filter(v => v !== null && v !== '').length;
  return respostas.filter(v => v !== null && v !== '').length;
}

function montarPerfilDaAnamnese(respostas) {
  const [
    dataNascimento,
    altura,
    pesoAtual,
    objetivoPrincipal,
    dificuldadeAlimentacao,
    atividadeFisica,
    responsavelCozinhar,
    comprometimento,
  ] = respostas || [];

  return {
    dataNascimento: dataNascimento || '',
    altura: altura || '',
    anamnese: {
      inicial: {
        pesoAtual: pesoAtual || '',
        objetivoPrincipal: objetivoPrincipal || '',
        dificuldadeAlimentacao: dificuldadeAlimentacao || '',
        atividadeFisica: atividadeFisica || '',
        responsavelCozinhar: responsavelCozinhar || '',
        comprometimento: comprometimento != null ? String(comprometimento) : '',
      },
      estiloVida: {
        objetivoPrincipal: objetivoPrincipal || '',
      },
      alimentar: {
        relacaoComComida: dificuldadeAlimentacao || '',
        responsavelCozinhar: responsavelCozinhar || '',
      },
      treino: {
        experienciaPrevia: atividadeFisica || '',
      },
    },
  };
}

function montarPerfilTreino(respostas) {
  const [rotina, sono, local, equipamentos, diasSemana, duracao, atividades, experiencia, aerobios, lesoes, dores, restricoes, objetivo, preferencias] = respostas || [];
  return {
    anamnese: {
      treino: {
        rotina: rotina || '', sono: sono || '', local: local || '', equipamentos: equipamentos || '',
        diasSemana: diasSemana || '', duracao: duracao || '', atividadesAtuais: atividades || '',
        experienciaPrevia: experiencia || '', aerobios: aerobios || '', lesoes: lesoes || '',
        dores: dores || '', restricoesMedicas: restricoes || '', objetivo: objetivo || '', preferencias: preferencias || '',
      },
      estiloVida: { rotina: rotina || '', sono: sono || '' },
    },
  };
}

function mergeDeep(base = {}, extra = {}) {
  const out = { ...base };
  Object.entries(extra).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      out[key] = mergeDeep(out[key] || {}, value);
    } else if (value !== '' && value !== null && value !== undefined) {
      out[key] = value;
    }
  });
  return out;
}

// Botão redondo de escala (0-4 e 0-10)
function BotaoEscala({ valor, ativo, onClick, largura }) {
  return (
    <button onClick={onClick}
      style={{
        width: largura || '40px', height: '40px', borderRadius: '50%',
        border: ativo ? '2px solid var(--accent)' : '1.5px solid var(--divider)',
        background: ativo ? 'var(--accent)' : 'var(--surface)',
        color: ativo ? 'var(--btn-text)' : 'var(--text-2)',
        fontSize: '13px', fontWeight: 700, cursor: 'pointer',
        fontFamily: "'Inter', sans-serif", transition: 'all 0.15s', flexShrink: 0,
      }}>
      {valor}
    </button>
  );
}

// Pílula de opção (múltipla escolha e frequência)
function BotaoOpcao({ texto, ativo, onClick }) {
  return (
    <button onClick={onClick}
      style={{
        padding: '9px 14px', borderRadius: '100px',
        border: ativo ? '2px solid var(--accent)' : '1.5px solid var(--divider)',
        background: ativo ? 'var(--accent)' : 'var(--surface)',
        color: ativo ? 'var(--btn-text)' : 'var(--text-2)',
        fontSize: '12px', fontWeight: 600, cursor: 'pointer', lineHeight: 1.3,
        fontFamily: "'Inter', sans-serif", transition: 'all 0.15s', textAlign: 'left',
      }}>
      {texto}
    </button>
  );
}

const inputStyle = {
  width: '100%', padding: '12px 14px', border: '1px solid rgba(151,142,134,.16)',
  borderRadius: '18px', fontSize: '14px', color: '#251c19',
  background: '#f4eee6', fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', sans-serif", outline: 'none', boxSizing: 'border-box',
};

const shellVars = {
  '--bg': '#fffaf4',
  '--surface': 'rgba(255,255,255,.78)',
  '--surface-2': '#f4eee6',
  '--divider': 'rgba(216,202,182,.72)',
  '--text': '#251c19',
  '--text-2': '#8c7f70',
  '--text-3': '#978e86',
  '--accent': '#895b40',
  '--btn-text': '#fff9f2',
  '--r-card': '28px',
  '--r-input': '18px',
  '--r-btn': '999px',
  '--sh-card': '0 12px 28px rgba(82,69,57,.055), inset 0 1px 0 rgba(255,255,255,.84)',
};

const shell = {
  page: { minHeight: '100vh', background: '#f3eee7', padding: '26px 14px 40px', fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Inter', sans-serif", ...shellVars },
  nav: { width: 'min(100%, 430px)', margin: '0 auto', background: '#fffaf4', borderRadius: '44px 44px 0 0', padding: '28px 22px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 0 0 1px rgba(255,255,255,.72), 0 28px 80px rgba(66,44,38,.10)' },
  back: { height: '42px', padding: '0 16px', border: '1px solid rgba(216,202,182,.86)', background: 'rgba(255,255,255,.56)', color: '#7f7165', borderRadius: '999px', fontSize: '13px', cursor: 'pointer' },
  content: { width: 'min(100%, 430px)', minHeight: 'calc(100vh - 56px)', margin: '0 auto', padding: '24px 22px 44px', background: '#fffaf4', borderRadius: '0 0 44px 44px', boxShadow: '0 28px 80px rgba(66,44,38,.10), inset 0 -1px 0 rgba(255,255,255,.95)' },
};

export default function AlunaQuestionario() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const { tipo } = useParams();
  const q = QUESTIONARIOS[tipo];
  const isPt = lang === 'pt';

  const [respostas, setRespostas] = useState(() => (q ? respostasVazias(q) : null));
  const [historico, setHistorico] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [erro, setErro] = useState('');
  const [respondendo, setRespondendo] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!q) { navigate('/aluna/questionarios', { replace: true }); return; }
    setRespostas(respostasVazias(q));
    setRespondendo(false);
    setSalvo(false);
  }, [tipo]); // eslint-disable-line

  const carregar = () => {
    if (!user?.id || !q) return;
    setLoading(true);
    supabase.from('questionario_respostas').select('*').eq('aluna_id', user.id).eq('tipo', tipo).order('created_at', { ascending: false })
      .then(({ data }) => { setHistorico(data || []); setLoading(false); });
  };

  useEffect(() => { carregar(); }, [user, tipo]); // eslint-disable-line

  if (!q || !respostas) return null;

  const TOTAL = totalItens(tipo);
  const respondidas = contarRespondidas(q, respostas);

  // Regras de envio: disbiose permite deixar em branco (mínimo 1);
  // nos demais, todos os itens são obrigatórios.
  const podeEnviar = q.modo === 'multipla'
    ? respondidas > 0
    : q.modo === 'aberto'
      ? q.perguntas.every((pergunta, idx) => !pergunta.obrigatoria || (respostas[idx] !== null && respostas[idx] !== ''))
      : respondidas === TOTAL;

  const total = q.modo === 'escala'
    ? Object.values(respostas).flat().reduce((acc, v) => acc + (v || 0), 0)
    : null;

  function responderSecao(secaoId, idx, valor) {
    setRespostas(prev => ({ ...prev, [secaoId]: prev[secaoId].map((v, i) => (i === idx ? valor : v)) }));
  }

  function responderPergunta(idx, valor) {
    setRespostas(prev => prev.map((v, i) => (i === idx ? valor : v)));
  }

  async function salvar() {
    if (!podeEnviar || salvando) return;
    setSalvando(true);
    setErro('');
    const { error } = await supabase.from('questionario_respostas').insert({
      aluna_id: user.id,
      tipo,
      respostas,
      total,
    });
    if (error) { setSalvando(false); setErro(isPt ? 'Não foi possível salvar. Tente novamente.' : 'Could not save. Please try again.'); return; }
    if (tipo === 'anamnese' || tipo === 'anamnese_treino') {
      try {
        const perfilDaAnamnese = tipo === 'anamnese' ? montarPerfilDaAnamnese(respostas) : montarPerfilTreino(respostas);
        const { data: perfilAtual } = await supabase
          .from('perfis')
          .select('*')
          .eq('aluna_id', user.id)
          .maybeSingle();

        await supabase.from('perfis').upsert({
          aluna_id: user.id,
          telefone: perfilAtual?.telefone || null,
          cidade: perfilAtual?.cidade || null,
          pais: perfilAtual?.pais || null,
          data_nascimento: perfilDaAnamnese.dataNascimento || perfilAtual?.data_nascimento || null,
          altura: perfilDaAnamnese.altura || perfilAtual?.altura || null,
          modalidade: perfilAtual?.modalidade || 'nutricao-treino',
          anamnese: mergeDeep(perfilAtual?.anamnese || {}, perfilDaAnamnese.anamnese),
        });
      } catch (syncError) {
        console.warn('Questionário salvo, mas não foi possível sincronizar com o perfil.', syncError);
      }
    }
    setSalvando(false);
    setSalvo(true);
    setRespondendo(false);
    setRespostas(respostasVazias(q));
    carregar();
  }

  const fmtDate = iso => new Date(iso).toLocaleDateString(isPt ? 'pt-BR' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div style={shell.page}>
      <style>{`
        @media(max-width:560px){
          body, #root { background:#fffaf4 !important; }
          .aluna-questionario-nav { width:100% !important; border-radius:0 !important; box-shadow:none !important; padding:calc(env(safe-area-inset-top, 0px) + 20px) 16px 0 !important; }
          .aluna-questionario-content { width:100% !important; min-height:calc(100dvh - 70px) !important; border-radius:0 !important; box-shadow:none !important; padding:22px 16px calc(34px + env(safe-area-inset-bottom, 0px)) !important; }
        }
      `}</style>
      <nav className="aluna-questionario-nav" style={shell.nav}>
        <img src="/logo.svg" alt="Team Carol Macedo" style={{ height:'30px', width:'auto', objectFit:'contain' }} />
        <button onClick={() => navigate('/aluna/questionarios')} style={shell.back}>
          {isPt ? 'Voltar' : 'Back'}
        </button>
      </nav>
      <div className="aluna-questionario-content" style={shell.content}>

        <h1 style={{ fontSize: '28px', color: 'var(--text)', margin: 0, fontWeight: 650, letterSpacing: 0 }}>{q.titulo}</h1>
        <p style={{ color: 'var(--text-2)', margin: '6px 0 24px', fontSize: '14px', lineHeight: 1.6 }}>
          {isPt
            ? (tipo === 'anamnese_treino' ? 'Formulário para personalização segura do seu treino pela Carol Macedo.' : 'Formulário de consulta da Nutricionista Barbara Macedo Bitencourt.')
            : (tipo === 'anamnese_treino' ? 'Form to safely personalize your training with Carol Macedo.' : 'Consultation form for Nutritionist Barbara Macedo Bitencourt.')}
        </p>

        {salvo && !respondendo && (
          <div style={{ background: 'var(--success-bg)', borderRadius: 'var(--r-input)', padding: '16px 20px', marginBottom: '20px', color: 'var(--success)', fontSize: '14px', fontWeight: 600 }}>
            {isPt
              ? '✓ Questionário enviado com sucesso! A equipe já pode visualizar suas respostas.'
              : '✓ Questionnaire submitted successfully. The team can now see your answers.'}
          </div>
        )}

        {!respondendo ? (
          <>
            <div style={{ background: 'var(--surface)', borderRadius: 'var(--r-card)', padding: '32px 24px', textAlign: 'center', marginBottom: '24px', boxShadow: 'var(--sh-card)' }}>
              <div style={{ width: '76px', height: '76px', borderRadius: '24px', background: '#e8d7c5', color: '#895b40', display: 'grid', placeItems: 'center', fontSize: '34px', margin: '0 auto 16px' }}>{q.icone}</div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>
                {historico.length === 0
                  ? (isPt ? 'Você ainda não respondeu este questionário.' : 'You have not answered this questionnaire yet.')
                  : (isPt ? 'Responder novamente' : 'Answer again')}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-2)', maxWidth: '380px', margin: '0 auto 20px', lineHeight: 1.6 }}>
                {q.descricaoCurta} {isPt ? `São ${TOTAL} ${q.secoes ? 'itens' : 'perguntas'}.` : `${TOTAL} ${q.secoes ? 'items' : 'questions'}.`}
              </div>
              <button onClick={() => { setRespondendo(true); setSalvo(false); }} style={{ padding: '12px 28px', background: 'var(--accent)', color: 'var(--btn-text)', border: 'none', borderRadius: 'var(--r-btn)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                {historico.length === 0 ? (isPt ? 'Começar a responder' : 'Start answering') : (isPt ? '+ Nova resposta' : '+ New answer')}
              </button>
            </div>

            {!loading && historico.length > 0 && (
              <div style={{ background: 'var(--surface)', borderRadius: 'var(--r-card)', overflow: 'hidden', boxShadow: 'var(--sh-card)' }}>
                <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--divider)', fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>{isPt ? 'Respostas enviadas' : 'Submitted answers'}</div>
                {historico.map(h => (
                  <div key={h.id} style={{ padding: '14px 24px', borderBottom: '1px solid var(--divider)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-3)', fontWeight: 500 }}>{fmtDate(h.created_at)}</span>
                    {h.total != null && (
                      <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent)', background: 'var(--surface-2)', padding: '4px 12px', borderRadius: '100px' }}>{isPt ? 'Pontuação' : 'Score'}: {h.total}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Instruções / legenda */}
            <div style={{ background: 'var(--surface)', borderRadius: 'var(--r-card)', padding: '24px', marginBottom: '20px', boxShadow: 'var(--sh-card)' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: q.legenda ? '12px' : 0, lineHeight: 1.6 }}>
                {q.instrucoes}
              </div>
              {q.legenda && q.legenda.map(l => (
                <div key={l.valor} style={{ display: 'flex', gap: '10px', alignItems: 'baseline', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent)', minWidth: '14px' }}>{l.valor}</span>
                  <span style={{ fontSize: '13px', color: 'var(--text-3)', lineHeight: 1.5 }}>{l.texto}</span>
                </div>
              ))}
            </div>

            {/* Modos com seções: escala (0-4) e frequência */}
            {q.secoes && q.secoes.map(secao => (
              <div key={secao.id} style={{ background: 'var(--surface)', borderRadius: 'var(--r-card)', marginBottom: '16px', overflow: 'hidden', boxShadow: 'var(--sh-card)' }}>
                <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--divider)', fontSize: '16px', fontWeight: 700, color: 'var(--accent)' }}>
                  {secao.titulo}
                </div>
                {secao.itens.map((item, idx) => (
                  <div key={idx} style={{ padding: '14px 24px', borderBottom: idx < secao.itens.length - 1 ? '1px solid var(--divider)' : 'none' }}>
                    <div style={{ fontSize: '13px', color: 'var(--text)', marginBottom: '10px', lineHeight: 1.5 }}>{item}</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {q.modo === 'escala' && [0, 1, 2, 3, 4].map(v => (
                        <BotaoEscala key={v} valor={v} ativo={respostas[secao.id][idx] === v} onClick={() => responderSecao(secao.id, idx, v)} />
                      ))}
                      {q.modo === 'frequencia' && q.opcoes.map(op => (
                        <BotaoOpcao key={op} texto={op} ativo={respostas[secao.id][idx] === op} onClick={() => responderSecao(secao.id, idx, op)} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* Modos com perguntas: aberto e múltipla escolha */}
            {q.perguntas && (
              <div style={{ background: 'var(--surface)', borderRadius: 'var(--r-card)', marginBottom: '16px', overflow: 'hidden', boxShadow: 'var(--sh-card)' }}>
                {q.perguntas.map((p, idx) => (
                  <div key={idx} style={{ padding: '16px 24px', borderBottom: idx < q.perguntas.length - 1 ? '1px solid var(--divider)' : 'none' }}>
                    <div style={{ fontSize: '13px', color: 'var(--text)', marginBottom: '4px', lineHeight: 1.5, fontWeight: 500 }}>
                      {idx + 1}) {p.obrigatoria && <span style={{ color: 'var(--error)' }}>* </span>}{p.label}
                    </div>
                    {p.obs && <div style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '8px', lineHeight: 1.5 }}>Obs: {p.obs}</div>}
                    <div style={{ marginTop: '8px' }}>
                      {/* múltipla escolha */}
                      {p.opcoes && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {p.opcoes.map(op => (
                            <BotaoOpcao key={op} texto={op} ativo={respostas[idx] === op} onClick={() => responderPergunta(idx, respostas[idx] === op ? null : op)} />
                          ))}
                        </div>
                      )}
                      {/* respostas abertas */}
                      {p.tipo === 'data' && (
                        <input type="date" value={respostas[idx] || ''} onChange={e => responderPergunta(idx, e.target.value || null)} style={{ ...inputStyle, maxWidth: '220px' }} />
                      )}
                      {p.tipo === 'texto' && (
                        <input type="text" value={respostas[idx] || ''} placeholder={p.placeholder || ''} onChange={e => responderPergunta(idx, e.target.value || null)} style={inputStyle} />
                      )}
                      {p.tipo === 'textolongo' && (
                        <textarea rows={3} value={respostas[idx] || ''} placeholder={p.placeholder || ''} onChange={e => responderPergunta(idx, e.target.value || null)} style={{ ...inputStyle, resize: 'vertical' }} />
                      )}
                      {p.tipo === 'escala10' && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {Array.from({ length: 11 }, (_, v) => (
                            <BotaoEscala key={v} valor={v} largura="36px" ativo={respostas[idx] === v} onClick={() => responderPergunta(idx, v)} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {erro && (
              <div style={{ background: 'var(--error-bg)', borderRadius: 'var(--r-input)', padding: '12px 16px', marginBottom: '16px', color: 'var(--error)', fontSize: '13px' }}>
                {erro}
              </div>
            )}

            {/* Barra de envio */}
            <div style={{ position: 'sticky', bottom: '0', background: 'rgba(255,250,244,.92)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', padding: '14px 0 0', borderTop: '1px solid rgba(216,202,182,.46)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <span style={{ fontSize: '12px', color: podeEnviar ? 'var(--success)' : 'var(--text-2)', fontWeight: 600 }}>
                {respondidas}/{TOTAL} {isPt ? 'respondidas' : 'answered'}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => { setRespondendo(false); setRespostas(respostasVazias(q)); }} style={{ padding: '11px 18px', background: 'transparent', border: '1px solid var(--divider)', borderRadius: 'var(--r-btn)', fontSize: '13px', color: 'var(--text-2)', cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                  {isPt ? 'Cancelar' : 'Cancel'}
                </button>
                <button onClick={salvar} disabled={!podeEnviar || salvando}
                  style={{ padding: '11px 24px', background: podeEnviar ? 'var(--accent)' : 'var(--divider)', color: 'var(--btn-text)', border: 'none', borderRadius: 'var(--r-btn)', fontSize: '13px', fontWeight: 600, cursor: podeEnviar ? 'pointer' : 'not-allowed', fontFamily: "'Inter', sans-serif" }}>
                  {salvando ? (isPt ? 'Salvando...' : 'Saving...') : (isPt ? 'Finalizar e salvar' : 'Finish and save')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
